import * as React from "react";
import { useScrollbarSize } from "@mihkeleidast/react-scrollbar-size";
import { Button } from "../button/button.mjs";
import debounce from "debounce";
import "tabbable";
import { useLabels } from "../helpers/label-provider/use-labels.mjs";
const ScrollFade = (props) => {
  const labels = useLabels();
  const { height, width } = useScrollbarSize();
  return /* @__PURE__ */ React.createElement(ScrollFadeInner, { ...props, labels, scrollBarHeight: height, scrollBarWidth: width });
};
class ScrollFadeInner extends React.Component {
  constructor(props) {
    super(props);
    this.element = null;
    this.handleFadeBeginning = () => {
      const { scrollLeft } = this.getElementSizes();
      if (this.element) {
        if (scrollLeft === 0 && this.state.fadeBeginning) {
          this.setState({
            fadeBeginning: false
          });
          if (this.props.onScrollToLeft) {
            this.props.onScrollToLeft();
          }
        } else if (scrollLeft !== 0 && !this.state.fadeBeginning) {
          this.setState({
            fadeBeginning: true
          });
        }
      }
    };
    this.handleFadeTop = () => {
      const { scrollTop } = this.getElementSizes();
      if (this.element) {
        if (scrollTop > 0 && !this.state.fadeTop) {
          this.setState({
            fadeTop: true
          });
        } else if (scrollTop === 0 && this.state.fadeTop) {
          this.setState({
            fadeTop: false
          });
          if (this.props.onScrollToTop) {
            this.props.onScrollToTop();
          }
        }
      }
    };
    this.handleFadeBottom = () => {
      const { scrollHeight, scrollTop, clientHeight } = this.getElementSizes();
      if (this.element) {
        if (scrollHeight - scrollTop - clientHeight <= 3 && this.state.fadeBottom) {
          this.setState({
            fadeBottom: false
          });
          if (this.props.onScrollToBottom) {
            this.props.onScrollToBottom();
          }
        } else if (scrollHeight - scrollTop - clientHeight > 3 && !this.state.fadeBottom) {
          this.setState({
            fadeBottom: true
          });
        }
      }
    };
    this.getElementSizes = () => {
      if (this.element) {
        return {
          scrollHeight: Math.round(this.element.scrollHeight),
          scrollWidth: Math.round(this.element.scrollWidth),
          scrollLeft: Math.round(this.element.scrollLeft),
          scrollTop: Math.round(this.element.scrollTop),
          clientHeight: this.element.clientHeight,
          clientWidth: this.element.clientWidth
        };
      }
      return {
        scrollHeight: 0,
        scrollWidth: 0,
        scrollLeft: 0,
        scrollTop: 0,
        clientHeight: 0,
        clientWidth: 0
      };
    };
    this.setElementRef = (el) => {
      this.element = el;
      if (this.props.elementRef) {
        this.props.elementRef(el);
      }
    };
    this.onScroll = this.onScroll.bind(this);
    this.debouncedResizeHandler = debounce(this.onScroll, 50);
    this.state = {
      fadeBeginning: false,
      fadeBottom: false,
      fadeEnd: false,
      fadeTop: false,
      isMounted: false
    };
  }
  componentDidMount() {
    this.setState(
      {
        isMounted: true
      },
      () => {
        this.onScroll();
      }
    );
    window.addEventListener("resize", this.debouncedResizeHandler);
  }
  componentDidUpdate() {
    this.onScroll();
  }
  componentWillUnmount() {
    window.removeEventListener("resize", this.debouncedResizeHandler);
    this.debouncedResizeHandler.clear();
  }
  BEM() {
    const classArray = ["scroll-fade"];
    if (this.state.fadeBeginning) {
      classArray.push("has-scrolled-to-right");
    }
    if (this.state.fadeBottom) {
      classArray.push("has-scrolled-to-top");
    }
    if (this.state.fadeEnd) {
      classArray.push("has-scrolled-to-left");
    }
    if (this.state.fadeTop) {
      classArray.push("has-scrolled-to-bottom");
    }
    if (this.hasArrows.enabled) {
      classArray.push("scroll-fade--has-arrows");
    }
    if (this.props.gutter) {
      classArray.push("scroll-fade--gutter-".concat(this.props.gutter));
    }
    if (this.props.className) {
      classArray.push(this.props.className);
    }
    return classArray.join(" ");
  }
  buttonBEM(direction) {
    const classArray = ["scroll-fade__button"];
    classArray.push("scroll-fade__button-" + direction);
    return classArray.join(" ");
  }
  onArrowClick(direction, event) {
    if (this.props.onArrowClick) {
      this.props.onArrowClick(event, direction);
    }
  }
  getArrowButton(direction) {
    const { focusable } = this.hasArrows;
    return /* @__PURE__ */ React.createElement(
      Button,
      {
        text: this.props.labels.get(direction),
        className: this.buttonBEM(direction),
        link: true,
        icon: "#arrow-".concat(direction),
        iconSize: "xs",
        onClick: (e) => {
          if (e.currentTarget instanceof HTMLButtonElement) {
            this.onArrowClick(direction, e);
          }
        },
        tabIndex: focusable ? void 0 : -1
      }
    );
  }
  onScroll() {
    this.handleFadeBeginning();
    this.handleFadeEnd();
    this.handleFadeTop();
    this.handleFadeBottom();
  }
  handleFadeEnd() {
    const { scrollWidth, scrollLeft, clientWidth } = this.getElementSizes();
    if (this.element) {
      if (scrollWidth - scrollLeft - clientWidth <= 3 && this.state.fadeEnd) {
        this.setState({
          fadeEnd: false
        });
        if (this.props.onScrollToRight) {
          this.props.onScrollToRight();
        }
      } else if (scrollWidth - scrollLeft - clientWidth > 3 && !this.state.fadeEnd) {
        this.setState({
          fadeEnd: true
        });
      }
    }
  }
  get hasArrows() {
    const { hasArrows } = this.props;
    if (hasArrows === void 0) {
      return {
        enabled: false,
        focusable: false
      };
    }
    if (typeof hasArrows === "boolean") {
      return {
        enabled: hasArrows,
        focusable: true
      };
    }
    return {
      focusable: true,
      ...hasArrows
    };
  }
  render() {
    const { fadeBeginning, fadeEnd, fadeTop, fadeBottom } = this.state;
    const hasArrows = this.hasArrows.enabled;
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        className: this.BEM(),
        style: {
          "--scroll-fade-scrollbar-width": "".concat(this.props.scrollBarWidth, "px"),
          "--scroll-fade-scrollbar-height": "".concat(this.props.scrollBarHeight, "px")
        }
      },
      fadeBeginning && hasArrows && this.getArrowButton("left"),
      fadeTop && hasArrows && this.getArrowButton("up"),
      /* @__PURE__ */ React.createElement("div", { className: "scroll-fade__inner", ref: this.setElementRef, onScroll: this.onScroll }, this.props.children),
      fadeBottom && hasArrows && this.getArrowButton("down"),
      fadeEnd && hasArrows && this.getArrowButton("right")
    );
  }
}
export {
  ScrollFade
};
